import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { graphql, withApollo } from 'react-apollo';
import {
  Message, Button, Confirm, Dimmer, Loader, Icon,
} from 'semantic-ui-react';

import LoadingDataEnhancer from './LoadingData';
import defaultCoverageOptOutsQuery from '../graphql/defaultCoverageOptOutsQuery';
import createDefaultCoverageOptOutMutation from '../graphql/createDefaultCoverageOptOutMutation';
import deleteDefaultCoverageOptOutMutation from '../graphql/deleteDefaultCoverageOptOutMutation';
import updateDefaultCoverageOptOutMutation from '../graphql/updateDefaultCoverageOptOutMutation';
import defaultCoverageMasterProductsQuery from '../graphql/defaultCoverageMasterProductsQuery';

import DefaultCoverageOptOutsNew from '../views/DefaultCoverageOptOuts/New';
import DefaultCoverageOptOutsEdit from '../views/DefaultCoverageOptOuts/Edit';
import DefaultCoverageOptOutsList from '../views/DefaultCoverageOptOuts/List';
// PARTY_SIDE is the form's own vocabulary for which side was chosen, and the form is
// what hands it back on the party-change callback.
import { PARTY_SIDE } from '../views/DefaultCoverageOptOuts/Form';
import NotFound from '../views/NotFound';
import Search from '../views/Shared/Search';
import Pagination from '../views/Shared/Pagination';
import { setSearch } from '../data/searchField/actions';
import useFeatureFlag from '../hooks/useFeatureFlag';
import { DB_DEFAULT_COVERAGE } from '../constants/featureFlags';
import {
  isStaleVersionError,
  STALE_VERSION_DELETE_HEADER,
  STALE_VERSION_DELETE_GUIDANCE,
  NO_MASTER_PRODUCTS_GUIDANCE,
  MASTER_PRODUCTS_ERROR_GUIDANCE,
  toProductOptions,
  hasNoMasterProducts as computeHasNoMasterProducts,
  hasMasterProductsError as computeHasMasterProductsError,
  buildCreateInputs,
  summariseCreateResults,
  describeCreateResults,
  createdSomething,
  alreadyOptedOutProductIds,
} from '../constants/defaultCoverageOptOut';

const PAGE_SIZE = 25;
const FIRST_PAGE = 1;
// One party can hold at most one row per master product, and a side has a handful of
// masters — so a single page is the whole answer, not the first slice of one.
const PARTY_LOOKUP_SIZE = 100;
const PARTY_NUMBER_PATTERN = /^\d{7}$/;
const AAN_PATTERN = /^\d{9}$/;

// Optimistic locking: both writes hand back the `version` the operator loaded
// so the server can refuse a write that would clobber a concurrent edit.
//
// startDate/endDate/notes are each included only when there is something to say
// (v2): APMS's updateById COALESCEs an absent field to "leave it alone", and
// `clearEndDate` is the one thing COALESCE cannot express, so an end date the
// operator cleared back to blank is sent as an explicit clear rather than as a
// null endDate — which the server would read as "untouched", not "gone".
export const buildUpdateInput = (optOut, values) => {
  const defaultCoverageOptOut = {};
  if (values.startDate) defaultCoverageOptOut.startDate = values.startDate;
  if (values.endDate) {
    defaultCoverageOptOut.endDate = values.endDate;
  } else if (optOut && optOut.endDate) {
    defaultCoverageOptOut.clearEndDate = true;
  }
  // Notes follow the same "absent means untouched" rule, but a clear CAN be said
  // without a flag: an empty string. null would not do it - tappy drops a null
  // argument and APMS COALESCEs a NULL back to the stored note - so a note the
  // operator emptied is sent as '' and only a row that never had one sends null.
  // The form hands empty notes back as null (create wants that), so whether this
  // is a clear is read against the loaded row here.
  if (values.notes) {
    defaultCoverageOptOut.notes = values.notes;
  } else {
    defaultCoverageOptOut.notes = optOut && optOut.notes ? '' : null;
  }

  return {
    defaultCoverageOptOutId: optOut.id,
    version: values.version != null ? values.version : optOut.version,
    defaultCoverageOptOut,
  };
};

export const buildDeleteInput = optOut => ({
  defaultCoverageOptOutId: optOut.id,
  version: optOut.version,
});

// Reads the three states of the master-products query out of the single prop
// Apollo hands in. Exported and unit-tested for the same reason buildUpdateInput
// is: it is the whole of the logic, and a full render of the errored query is
// not a reliable place to assert it.
//
// The states are loading / FAILED / came-back-empty. All three arrive with an
// empty options array, so failure has to be read from `error` and kept apart -
// otherwise a broken load is reported to Ops as "nothing is seeded", which sends
// them off to seed a product that may already be seeded.
export const readMasterProductsState = masterProductsData => {
  const loading = Boolean(masterProductsData && masterProductsData.loading);
  const failed = Boolean(masterProductsData && masterProductsData.error);
  const productOptions = toProductOptions(
    masterProductsData && masterProductsData.defaultCoverageMasterProducts
  );

  return {
    // The raw list as well as the mapped options: the multi-select picker filters
    // by masterType (party side) and orders by masterPriority, and neither
    // survives a mapping to {key, value, text}.
    masterProducts: (masterProductsData && masterProductsData.defaultCoverageMasterProducts) || [],
    productOptions,
    productsLoading: loading,
    hasNoMasterProducts: computeHasNoMasterProducts(
      productOptions,
      loading,
      failed
    ),
    masterProductsError: computeHasMasterProductsError(failed, loading),
  };
};

const DefaultCoverageOptOuts = ({
  data,
  client,
  masterProductsData,
  createMutation,
  deleteMutation,
  updateMutation,
  searchFields,
  setSearchFields: setSearchFieldsProp,
}) => {
  const [currentPage, setCurrentPage] = useState(FIRST_PAGE);
  const [
    selectedDefaultCoverageOptOut,
    setSelectedDefaultCoverageOptOut,
  ] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isNewModalOpen, setIsNewModalOpen] = useState(false);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false);
  const [newFormError, setNewFormError] = useState(null);
  const [newFormSubmitting, setNewFormSubmitting] = useState(false);
  const [editFormError, setEditFormError] = useState(null);
  const [editFormSubmitting, setEditFormSubmitting] = useState(false);
  // Kept apart from the generic form/flash errors: a lost version race needs
  // its own loud "your change was not applied, refresh" treatment.
  const [editStaleVersionError, setEditStaleVersionError] = useState(null);
  const [deleteStaleVersionError, setDeleteStaleVersionError] = useState(null);
  // A delete in flight. The ref is the guard (a second click lands before the
  // state update re-renders the disabled button); the state drives the button.
  // Without it a double-click sends the same version twice and the loser comes
  // back as a stale-version notice for a delete that actually succeeded.
  const deletingRef = useRef(false);
  const [deleting, setDeleting] = useState(false);
  const [flashMessage, setFlashMessage] = useState(null);
  // Green only when a row was actually written. A submit that found everything
  // already opted out changed nothing, and a positive flash claimed otherwise.
  const [flashIsPositive, setFlashIsPositive] = useState(true);
  // Products the party typed into the CREATE form is already opted out of, so the
  // picker can grey them and the fall-through preview can tell the truth. Looked up
  // per party rather than read off the list, because the list is paginated and
  // filtered: a party's existing row is frequently not on the page in view.
  const [partyOptedOutProductIds, setPartyOptedOutProductIds] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const searchVariablesRef = useRef({ buyerNumber: null, sellerNumber: null, auctionAccessNumber: null });

  // The picker offers ONLY products that have a master (default coverage)
  // subscription seeded - not the catalog - because an opt-out naming a
  // product with no master is inert: saved, live-looking in the list, and
  // suppressing nothing. See readMasterProductsState for the three states.
  const {
    masterProducts,
    productsLoading: masterProductsLoading,
    hasNoMasterProducts,
    masterProductsError,
  } = readMasterProductsState(masterProductsData);

  const refetchCurrentPage = () => data.refetch({
    ...searchVariablesRef.current,
    page: currentPage,
    size: PAGE_SIZE,
  });

  const onSearchSubmitHandler = event => {
    const { buyerNumber, sellerNumber, auctionAccessNumber } = event.target;
    const variableArguments = {
      buyerNumber: (buyerNumber.value || '').trim() || null,
      sellerNumber: (sellerNumber.value || '').trim() || null,
      auctionAccessNumber: (auctionAccessNumber.value || '').trim() || null,
    };
    searchVariablesRef.current = variableArguments;
    setCurrentPage(FIRST_PAGE);
    setIsSearching(true);
    data
      .refetch({ ...variableArguments, page: FIRST_PAGE, size: PAGE_SIZE })
      .finally(() => setIsSearching(false));
    setSearchFieldsProp(variableArguments);
  };

  const onSelectDefaultCoverageOptOut = (action, defaultCoverageOptOut) => {
    setSelectedDefaultCoverageOptOut(defaultCoverageOptOut);
    switch (action) {
      case 'Edit':
        setIsEditModalOpen(true);
        break;
      case 'Delete':
        setIsDeleteConfirmationOpen(true);
        break;
      default:
        setSelectedDefaultCoverageOptOut(null);
    }
  };

  // Re-ask for the master products at the moment the form is opened, not just when
  // the page mounted. The picker is fed entirely by that query, and a master added on
  // the Masters screen while this page was already open would otherwise be missing
  // from the list with nothing to indicate it — the operator sees a short list that
  // looks correct. network-only on mount covers navigating in; this covers staying.
  const openNewForm = () => {
    if (masterProductsData && masterProductsData.refetch) {
      // Caught, not awaited: the form opens either way. A refetch that fails leaves the
      // last good list on screen, which is far better than an unhandled rejection or a
      // blank picker — and APMS still refuses anything the stale list would allow.
      Promise.resolve(masterProductsData.refetch()).catch(() => {});
    }
    setIsNewModalOpen(true);
  };

  const onNewFormCloseHandler = () => {
    setPartyOptedOutProductIds([]);
    setNewFormError(null);
    setNewFormSubmitting(false);
    setIsNewModalOpen(false);
  };

  // Create is a BATCH: one party, N products, one operator action. Fall-through
  // means fully excluding a party requires a row per product, so submitting them
  // one modal at a time was the wrong shape.
  //
  // The batch can partially succeed, and that has to stay legible — reporting
  // "Error while creating" over the whole thing would hide which products landed.
  // An already-existing row is a NO-OP rather than a failure: re-submitting a
  // party whose first product was already opted out is the normal way to add the
  // rest.
  // Called by the create form when the party becomes identifiable (a side plus a
  // well-formed identifier). A partial number is not queried - it would be a
  // request per keystroke for an answer that cannot be right yet.
  //
  // The buyer branch (v2) is identifiable by buyerNumber, auctionAccessNumber, or
  // both — whichever are well-formed are sent, ANDed by the server, so a lookup
  // with only one populated still narrows correctly rather than over- or
  // under-matching the other two legal buyer-branch shapes.
  const lookupPartyOptOuts = (partySide, identifiers) => {
    const buyer = String((identifiers && identifiers.buyerNumber) || '').trim();
    const seller = String((identifiers && identifiers.sellerNumber) || '').trim();
    const aan = String((identifiers && identifiers.auctionAccessNumber) || '').trim();
    const buyerOk = PARTY_NUMBER_PATTERN.test(buyer);
    const sellerOk = PARTY_NUMBER_PATTERN.test(seller);
    const aanOk = AAN_PATTERN.test(aan);
    // Seller and seller-paid both submit sellerNumber alone (same column, same
    // isSuppressed branch) — anything that is not the buyer side is looked up
    // the same way.
    const identifiable = partySide !== PARTY_SIDE.BUYER ? sellerOk : buyerOk || aanOk;

    if (!partySide || !identifiable) {
      // Guarded: assigning a fresh [] would be a new array identity every time and
      // re-render on every keystroke of a partial identifier for no change in meaning.
      setPartyOptedOutProductIds(previous => (previous.length === 0 ? previous : []));
      return;
    }
    const variables = partySide !== PARTY_SIDE.BUYER
      ? { sellerNumber: seller, page: FIRST_PAGE, size: PARTY_LOOKUP_SIZE }
      : {
        ...(buyerOk ? { buyerNumber: buyer } : {}),
        ...(aanOk ? { auctionAccessNumber: aan } : {}),
        page: FIRST_PAGE,
        size: PARTY_LOOKUP_SIZE,
      };

    client
      .query({
        query: defaultCoverageOptOutsQuery,
        variables,
        fetchPolicy: 'network-only',
      })
      .then(({ data: lookupData }) => {
        const rows = (lookupData
          && lookupData.defaultCoverageOptOuts
          && lookupData.defaultCoverageOptOuts.items) || [];
        setPartyOptedOutProductIds(alreadyOptedOutProductIds(rows, {
          buyerNumber: buyerOk ? buyer : null,
          sellerNumber: sellerOk ? seller : null,
          auctionAccessNumber: aanOk ? aan : null,
        }));
      })
      // A failed lookup must not block the operator: the picker falls back to
      // offering everything, and APMS's duplicate guard still refuses a repeat.
      .catch(() => setPartyOptedOutProductIds([]));
  };

  const onNewFormSubmitHandler = values => {
    if (masterProductsError) {
      setNewFormError(MASTER_PRODUCTS_ERROR_GUIDANCE);
      return;
    }
    if (hasNoMasterProducts) {
      setNewFormError(NO_MASTER_PRODUCTS_GUIDANCE);
      return;
    }

    const inputs = buildCreateInputs({
      partySide: values.partySide,
      buyerNumber: values.buyerNumber,
      sellerNumber: values.sellerNumber,
      auctionAccessNumber: values.auctionAccessNumber,
      productIds: values.productIds,
      startDate: values.startDate,
      endDate: values.endDate,
      notes: values.notes,
    });
    if (inputs.length === 0) return;

    const codeFor = productId => {
      const product = (masterProducts || []).find(
        candidate => String(candidate.productId) === String(productId)
      );
      return (product && product.productCode) || String(productId);
    };

    setNewFormSubmitting(true);

    // Sequential, not Promise.all: each create takes a SERIALIZABLE transaction in
    // APMS whose duplicate pre-check is the only duplicate guard there is, and
    // firing them together is exactly the contention that guard exists to
    // serialize. A handful of products does not need the parallelism.
    inputs
      .reduce(
        (chain, input) => chain.then(acc => createMutation({ variables: { input } })
          .then(({ data: responseData }) => {
            const { createDefaultCoverageOptOut: { success, errors } } = responseData;
            return [...acc, {
              productCode: codeFor(input.defaultCoverageOptOut.productId),
              success,
              error: errors && errors[0],
            }];
          })
          .catch(() => [...acc, {
            productCode: codeFor(input.defaultCoverageOptOut.productId),
            success: false,
            error: 'request failed',
          }])),
        Promise.resolve([])
      )
      .then(results => {
        setNewFormSubmitting(false);
        const summary = summariseCreateResults(results);
        setFlashMessage(describeCreateResults(summary));
        setFlashIsPositive(createdSomething(summary));
        refetchCurrentPage();
        if (summary.allSucceeded) {
          onNewFormCloseHandler();
        } else {
          // Leave the form open on a partial failure so the operator can see what
          // is still missing against the list they just ticked.
          setNewFormError(describeCreateResults(summary));
        }
      });
  };

  const onEditFormCloseHandler = () => {
    setEditFormError(null);
    setEditStaleVersionError(null);
    setEditFormSubmitting(false);
    setIsEditModalOpen(false);
  };

  // The only way forward after a lost version race: pull the current rows, drop
  // the stale notices and close the modal, since the form still holds the
  // version the server just rejected.
  const refreshAfterStaleVersion = () => {
    setEditStaleVersionError(null);
    setDeleteStaleVersionError(null);
    setEditFormError(null);
    setIsEditModalOpen(false);
    setSelectedDefaultCoverageOptOut(null);
    refetchCurrentPage();
  };

  const onEditFormSubmitHandler = values => {
    setEditFormSubmitting(true);
    setEditStaleVersionError(null);
    updateMutation({
      variables: {
        input: buildUpdateInput(selectedDefaultCoverageOptOut, values),
      },
    })
      .then(({ data: responseData }) => {
        const {
          updateDefaultCoverageOptOut: { success, errors, errorCode },
        } = responseData;
        if (success) {
          setEditFormSubmitting(false);
          setFlashIsPositive(true);
          setFlashMessage('Successfully updated default coverage opt-out');
          refetchCurrentPage();
          onEditFormCloseHandler();
          return;
        }
        const serverError = errors && errors[0];
        setEditFormSubmitting(false);
        // A stale version is not a generic validation failure - the operator's
        // change never landed and re-sending the same version never will, so it
        // goes to the loud in-modal notice with the refresh action instead of
        // the plain red box.
        if (isStaleVersionError(errorCode, serverError)) {
          setEditStaleVersionError(serverError);
        } else {
          setEditFormError(
            serverError || 'Error while updating default coverage opt-out'
          );
        }
      })
      .catch(() => {
        setEditFormSubmitting(false);
        setEditFormError('Error while updating default coverage opt-out');
      });
  };

  const finishDelete = () => {
    deletingRef.current = false;
    setDeleting(false);
    setIsDeleteConfirmationOpen(false);
  };

  const handleDeleteConfirm = () => {
    if (deletingRef.current) return;
    deletingRef.current = true;
    setDeleting(true);
    setDeleteStaleVersionError(null);
    deleteMutation({
      variables: {
        input: buildDeleteInput(selectedDefaultCoverageOptOut),
      },
    })
      .then(({ data: responseData }) => {
        const {
          deleteDefaultCoverageOptOut: { success, errors, errorCode },
        } = responseData;
        if (success) {
          setFlashIsPositive(true);
          setFlashMessage('Successfully deleted default coverage opt-out');
          refetchCurrentPage();
        } else {
          const serverError = errors && errors[0];
          // The confirmation dialog is gone by now, so the stale-version notice
          // has to live on the page itself.
          if (isStaleVersionError(errorCode, serverError)) {
            setDeleteStaleVersionError(serverError);
          } else {
            setFlashIsPositive(false);
            setFlashMessage(
              serverError || 'Failed to delete default coverage opt-out'
            );
          }
        }
        finishDelete();
      })
      .catch(() => {
        finishDelete();
        setFlashIsPositive(false);
        setFlashMessage('Failed to delete default coverage opt-out');
      });
  };

  const handleDeleteCancel = () => {
    if (deletingRef.current) return;
    setIsDeleteConfirmationOpen(false);
  };

  const handleDismiss = () => setFlashMessage(null);

  const goToNextPage = () => {
    const defaultCoverageOptOuts = data.defaultCoverageOptOuts || {};
    const total = defaultCoverageOptOuts.total || 0;
    const nextPage = currentPage + 1;
    if (currentPage * PAGE_SIZE < total) {
      setCurrentPage(nextPage);
      data.refetch({
        ...searchVariablesRef.current,
        page: nextPage,
        size: PAGE_SIZE,
      });
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > FIRST_PAGE) {
      const prevPage = currentPage - 1;
      setCurrentPage(prevPage);
      data.refetch({
        ...searchVariablesRef.current,
        page: prevPage,
        size: PAGE_SIZE,
      });
    }
  };

  const { defaultCoverageOptOuts, error } = data;
  const items = (defaultCoverageOptOuts && defaultCoverageOptOuts.items) || [];
  const total = (defaultCoverageOptOuts && defaultCoverageOptOuts.total) || 0;
  const start = items.length > 0 ? (currentPage - 1) * PAGE_SIZE + 1 : 0;
  const end = start > 0 ? start + items.length - 1 : 0;
  const hasNext = end < total;

  return (
    <div>
      {error && (
        <Message
          negative
          content={error.message || 'An error occurred while loading data.'}
        />
      )}
      {flashMessage && (
        <Message
          data-testid='default-coverage-opt-out-flash-message'
          onDismiss={handleDismiss}
          content={flashMessage}
          positive={flashIsPositive}
          warning={!flashIsPositive}
        />
      )}
      {deleteStaleVersionError && (
        <Message
          negative
          icon
          data-testid='default-coverage-opt-out-stale-version-message'
        >
          <Icon name='warning sign' />
          <Message.Content>
            <Message.Header>{STALE_VERSION_DELETE_HEADER}</Message.Header>
            <p>{deleteStaleVersionError}</p>
            <p>{STALE_VERSION_DELETE_GUIDANCE}</p>
            <Button
              data-testid='default-coverage-opt-out-stale-version-refresh'
              color='blue'
              onClick={refreshAfterStaleVersion}
              size='small'
              inverted
            >
              <Icon name='refresh' /> Refresh list
            </Button>
          </Message.Content>
        </Message>
      )}
      <Button
        data-testid='default-coverage-opt-out-create-button'
        color='green'
        onClick={openNewForm}
        inverted
      >
        {' '}
        Create New Default Coverage Opt-Out
      </Button>

      <br />
      <br />

      <Search
        onSubmit={onSearchSubmitHandler}
        searchFields={searchFields}
        searchOptions={['buyerNumber', 'sellerNumber', 'auctionAccessNumber']}
        multipleSearches
      />

      {isNewModalOpen && (
        <div>
          <DefaultCoverageOptOutsNew
            isModalOpen={isNewModalOpen}
            onCloseHandler={onNewFormCloseHandler}
            onSubmitHandler={onNewFormSubmitHandler}
            error={newFormError}
            submitting={newFormSubmitting}
            masterProducts={masterProducts}
            alreadyOptedOutProductIds={partyOptedOutProductIds}
            onPartyChange={lookupPartyOptOuts}
            productsLoading={masterProductsLoading}
            hasNoMasterProducts={hasNoMasterProducts}
            masterProductsError={masterProductsError}
          />
        </div>
      )}

      {isEditModalOpen && (
        <div>
          <DefaultCoverageOptOutsEdit
            isModalOpen={isEditModalOpen}
            onCloseHandler={onEditFormCloseHandler}
            onSubmitHandler={onEditFormSubmitHandler}
            error={editFormError}
            submitting={editFormSubmitting}
            masterProducts={masterProducts}
            staleVersionError={editStaleVersionError}
            onRefreshHandler={refreshAfterStaleVersion}
            defaultCoverageOptOut={selectedDefaultCoverageOptOut}
          />
        </div>
      )}

      <Dimmer.Dimmable dimmed={isSearching}>
        <Dimmer active={isSearching} inverted>
          <Loader>Searching...</Loader>
        </Dimmer>

        <DefaultCoverageOptOutsList
          onSelectDefaultCoverageOptOut={onSelectDefaultCoverageOptOut}
          defaultCoverageOptOuts={items}
        />

        <Pagination
          start={start}
          end={end}
          nextHandler={goToNextPage}
          previousHandler={goToPreviousPage}
          loading={data.loading}
          lastPage={!hasNext && items.length !== 0}
          pageSize={PAGE_SIZE}
        />
      </Dimmer.Dimmable>

      <Confirm
        data-testid='default-coverage-opt-out-delete-confirm'
        open={isDeleteConfirmationOpen}
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        content={
          'Are you sure you want to delete this default coverage opt-out? '
          + 'Default coverage will apply to this party and product again on future sales.'
        }
        cancelButton={{
          content: 'Cancel',
          'data-testid': 'default-coverage-opt-out-delete-cancel',
        }}
        confirmButton={{
          content: 'OK',
          'data-testid': 'default-coverage-opt-out-delete-ok',
          disabled: deleting,
          loading: deleting,
        }}
      />
    </div>
  );
};

DefaultCoverageOptOuts.propTypes = {
  // Injected by withApollo. Used for the per-party opt-out lookup, which cannot be a
  // graphql() HOC: its variables come from what the operator typed into the form, and
  // an HOC only sees the component's incoming props.
  client: PropTypes.shape({ query: PropTypes.func }),
  data: PropTypes.shape({
    refetch: PropTypes.func.isRequired,
    defaultCoverageOptOuts: PropTypes.shape({
      total: PropTypes.number,
      items: PropTypes.array,
    }),
    error: PropTypes.object,
    loading: PropTypes.bool,
  }).isRequired,
  masterProductsData: PropTypes.shape({
    defaultCoverageMasterProducts: PropTypes.array,
    loading: PropTypes.bool,
    // Apollo sets this when the query failed. Read because a failure and an
    // empty result both arrive as an empty list, and they must not read alike.
    error: PropTypes.object,
    // Called when the create form opens, so the picker reflects masters added
    // while this page was already on screen.
    refetch: PropTypes.func,
  }),
  createMutation: PropTypes.func.isRequired,
  deleteMutation: PropTypes.func.isRequired,
  updateMutation: PropTypes.func.isRequired,
  searchFields: PropTypes.object,
  setSearchFields: PropTypes.func.isRequired,
};

DefaultCoverageOptOuts.defaultProps = {
  masterProductsData: null,
  searchFields: null,
};

// Unwrapped (no Apollo/router/flag HOCs), for component tests only.
export { DefaultCoverageOptOuts as DefaultCoverageOptOutsView };

const mapDispatchToProps = dispatch => ({
  setSearchFields: fields => {
    dispatch(setSearch(fields));
  },
});

const mapStateToProps = state => {
  const {
    data: { searchFields },
  } = state;
  return { searchFields };
};

const DefaultCoverageOptOutsContent = compose(
  withRouter,
  graphql(defaultCoverageOptOutsQuery, {
    options: {
      fetchPolicy: 'network-only',
      variables: { page: FIRST_PAGE, size: PAGE_SIZE },
    },
  }),
  // network-only, like every other query on this screen and all three on the masters
  // screen. It was the one query left on Apollo's default cache-first, and the picker
  // is fed entirely by it: add a master on the Masters screen, come back here, and the
  // list replayed the cached answer from before the master existed. The new product was
  // simply absent — no error, no empty state, just a short list that looked correct.
  graphql(defaultCoverageMasterProductsQuery, {
    name: 'masterProductsData',
    options: { fetchPolicy: 'network-only' },
  }),
  graphql(createDefaultCoverageOptOutMutation, { name: 'createMutation' }),
  graphql(deleteDefaultCoverageOptOutMutation, { name: 'deleteMutation' }),
  graphql(updateDefaultCoverageOptOutMutation, { name: 'updateMutation' }),
  LoadingDataEnhancer,
  withApollo,
  connect(mapStateToProps, mapDispatchToProps)
)(DefaultCoverageOptOuts);

// Gate the section on the DB feature flag: null while flags load, NotFound when
// disabled, otherwise the real page. The gate sits outside the Apollo enhancers
// so no opt-out query is issued while the flag is off. Access is enforced by the
// authorize() HOC wrapping this component in App.js.
const DefaultCoverageOptOutsPage = () => {
  const [isDefaultCoverageOptOutEnabled, loaded] = useFeatureFlag(
    DB_DEFAULT_COVERAGE
  );

  if (!loaded) return null;
  if (!isDefaultCoverageOptOutEnabled) return <NotFound />;

  return <DefaultCoverageOptOutsContent />;
};

export default DefaultCoverageOptOutsPage;
